import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';

import ConfirmModal from '../../../../../Components/crude/ConfirmModal';
import * as api from '../procurementApi';
import { DOC_SOURCE } from '../procurementConstants';
import { formatDateTime } from '../../formatters';

// Bytes → label ringkas untuk baris file di popup Master Document (mengikuti
// popup yang sama di Planning SupportingDocumentTab).
const formatFileSize = bytes => {
  if (!bytes || bytes <= 0) return '';
  return bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// Procurement > Supporting Document tab (ProcurementValidity contract §3–8).
//
// Two lists:
//   1. List Data Document Planning     — echo from Planning, always VIEW ONLY
//      (detail.supportingDocuments).
//   2. List Data Document Requirement  — this stage's own documents[], editable
//      in Edit mode via live calls (Master picker / custom upload / delete /
//      Show to DMUT), view-only in Detail mode (detail.requirementDocuments).
//
// Completing the tab (Save & Completed) is handled by ProcurementForm; here we
// only mutate the document collection, then pull fresh server truth.

// Read-only rendering of a document list (Planning list always; Requirement list
// in Detail mode). `onView` is optional — with it, the View button downloads.
const ReadonlyDocList = ({ title, docs, onView }) => (
  <>
    <h6
      className='text-uppercase fw-semibold mb-3 fs-12'
      style={{ letterSpacing: '0.5px' }}
    >
      {title}
    </h6>
    {docs.length === 0 ? (
      <div className='sd-empty mb-4'>
        <i className='ri-file-text-line'></i>
        <p>No documents</p>
      </div>
    ) : (
      <div className='mb-4'>
        <div className='sd-doc-header'>
          <span style={{ flex: '2 1 0' }}>Document Name</span>
          <span style={{ flex: '1 1 0' }}>Source</span>
          <span style={{ flex: '1 1 0' }}>Last Updated</span>
          <span style={{ flex: '1 1 0', textAlign: 'center' }}>
            Show to DMUT?
          </span>
          <span style={{ flex: '1.4 1 0', textAlign: 'right' }}>Action</span>
        </div>
        {docs.map(d => (
          <div key={d.id} className='sd-doc-row'>
            <div style={{ flex: '2 1 0' }}>
              <span className='fw-semibold fs-13'>{d.name}</span>
            </div>
            <div style={{ flex: '1 1 0' }}>
              <span
                className={`sd-badge ${
                  d.source === DOC_SOURCE.MASTER ? 'master' : 'custom'
                }`}
              >
                {d.source === DOC_SOURCE.MASTER
                  ? 'Master Document'
                  : 'Upload Custom'}
              </span>
            </div>
            <div style={{ flex: '1 1 0' }}>
              <span className='text-muted fs-12'>
                {formatDateTime(d.lastUpdated)}
              </span>
            </div>
            <div
              className='form-check form-switch form-switch-secondary d-flex justify-content-center mb-0'
              style={{ flex: '1 1 0' }}
            >
              <input
                className='form-check-input'
                type='checkbox'
                role='switch'
                checked={!!d.showToDmut}
                disabled
                readOnly
              />
            </div>
            <div style={{ flex: '1.4 1 0', textAlign: 'right' }}>
              <div className='d-flex gap-1 justify-content-end'>
                <button className='sd-btn-ghost' onClick={() => onView?.(d)}>
                  <i className='ri-eye-line'></i> View
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </>
);

const ProcurementSupportingDocumentTab = ({
  procurementValidityId,
  planningId,
  detail,
  setDetail,
  refresh,
  readOnly = false,
}) => {
  const planningDocs = detail.supportingDocuments || [];
  const reqDocs = detail.requirementDocuments || [];
  const fileInputRef = useRef(null);

  const [busy, setBusy] = useState(false);

  const [masterModal, setMasterModal] = useState(false);
  const [masterRows, setMasterRows] = useState([]);
  const [masterSearch, setMasterSearch] = useState('');
  const [masterLoading, setMasterLoading] = useState(false);
  const [checkedMaster, setCheckedMaster] = useState({});

  const [customModal, setCustomModal] = useState(false);
  const [customFile, setCustomFile] = useState(null);
  const [customName, setCustomName] = useState('');
  const [customShowToDmut, setCustomShowToDmut] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  const reloadReqDocs = useCallback(async () => {
    const fresh = await refresh();
    setDetail(prev => ({
      ...prev,
      requirementDocuments: fresh.requirementDocuments,
    }));
  }, [refresh, setDetail]);

  // ---- Master Document picker (from CrudeDocumentUpload master list) --------
  const loadMaster = useCallback(search => {
    setMasterLoading(true);
    api
      .lookupMasterDocuments(search)
      .then(setMasterRows)
      .catch(error => toast.error(`Failed to load master documents: ${error}`))
      .finally(() => setMasterLoading(false));
  }, []);

  const openMasterModal = () => {
    setCheckedMaster({});
    setMasterSearch('');
    setMasterModal(true);
    loadMaster('');
  };

  // Debounce the master search box.
  useEffect(() => {
    if (!masterModal) return undefined;
    const t = setTimeout(() => loadMaster(masterSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [masterSearch, masterModal, loadMaster]);

  const toggleMaster = id =>
    setCheckedMaster(prev => ({ ...prev, [id]: !prev[id] }));

  const addFromMaster = async () => {
    const selected = masterRows.filter(m => checkedMaster[m.id]);
    if (selected.length === 0) {
      toast.error('Select at least one document');
      return;
    }
    setBusy(true);
    try {
      await api.addDocumentsFromMaster(
        procurementValidityId,
        selected.map(m => ({ crudeDocumentUploadId: m.id, showToDmut: true }))
      );
      await reloadReqDocs();
      toast.success(`${selected.length} document(s) added`);
      setMasterModal(false);
    } catch (error) {
      toast.error(`Failed to add documents: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  // ---- Upload Custom File modal -------------------------------------------
  const onBrowse = () => fileInputRef.current?.click();

  const beginCustom = file => {
    if (!file) return;
    setCustomFile(file);
    setCustomName(file.name.replace(/\.[^.]+$/, ''));
    setCustomShowToDmut(true);
    setCustomModal(true);
  };

  const onFilePicked = e => {
    beginCustom(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    beginCustom(e.dataTransfer.files?.[0]);
  };

  const addCustom = async () => {
    if (!customName.trim()) {
      toast.error('Document Name is required');
      return;
    }
    if (!customFile) {
      toast.error('A file is required');
      return;
    }
    setBusy(true);
    try {
      await api.uploadDocument(procurementValidityId, {
        documentName: customName.trim(),
        showToDmut: customShowToDmut,
        file: customFile,
      });
      await reloadReqDocs();
      toast.success('Document uploaded');
      setCustomModal(false);
      setCustomFile(null);
      setCustomName('');
    } catch (error) {
      toast.error(`Upload failed: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleShowToDmut = async doc => {
    const next = !doc.showToDmut;
    // Optimistic — reflect immediately, roll back on failure.
    setDetail(prev => ({
      ...prev,
      requirementDocuments: prev.requirementDocuments.map(d =>
        d.id === doc.id ? { ...d, showToDmut: next } : d
      ),
    }));
    try {
      await api.setDocumentShowToDmut(procurementValidityId, doc.id, next);
    } catch (error) {
      toast.error(`Failed to update Show to DMUT: ${error}`);
      setDetail(prev => ({
        ...prev,
        requirementDocuments: prev.requirementDocuments.map(d =>
          d.id === doc.id ? { ...d, showToDmut: doc.showToDmut } : d
        ),
      }));
    }
  };

  const confirmDelete = async () => {
    const docId = deleteId;
    setDeleteId(null);
    setBusy(true);
    try {
      await api.deleteDocument(procurementValidityId, docId);
      await reloadReqDocs();
      toast.success('Document removed');
    } catch (error) {
      toast.error(`Failed to remove document: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const openBlob = blob => {
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  // Requirement docs (this stage's own `documents[]`) — ProcurementValidity §7.
  const handleView = async doc => {
    try {
      openBlob(await api.downloadDocument(procurementValidityId, doc.id));
    } catch (error) {
      toast.error(`Failed to open document: ${error}`);
    }
  };

  // Planning docs are a read-only echo with no download route here — go through
  // Planning's own endpoint via planningId (contract 2026-07-23).
  const handleViewPlanning = async doc => {
    try {
      openBlob(await api.downloadPlanningDocument(planningId, doc.id));
    } catch (error) {
      toast.error(`Failed to open document: ${error}`);
    }
  };

  return (
    <React.Fragment>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div>
          <h5 className='fw-semibold mb-1'>
            <i className='ri-file-list-3-line me-2'></i>
            Document Information
          </h5>
          <p className='text-muted mb-0 fs-12'>
            Supporting documents for this procurement.
          </p>
        </div>
      </div>

      <Card className='border shadow-sm'>
        <CardBody>
          {/* Planning documents — always read-only (download via Planning) */}
          <ReadonlyDocList
            title='List Data Document Planning'
            docs={planningDocs}
            onView={handleViewPlanning}
          />

          {/* Requirement documents — editable in Edit mode */}
          {readOnly ? (
            <ReadonlyDocList
              title='List Data Document Requirement'
              docs={reqDocs}
              onView={handleView}
            />
          ) : (
            <Row>
              <Col sm={3}>
                <h6
                  className='text-uppercase fw-semibold mb-3 fs-12'
                  style={{ letterSpacing: '0.5px' }}
                >
                  Upload Document
                </h6>
                <div
                  className='sd-upload-card mb-3'
                  role='button'
                  onClick={openMasterModal}
                >
                  <div className='sd-upload-card-icon master'>
                    <i className='ri-database-2-line'></i>
                  </div>
                  <div className='sd-upload-card-text'>
                    <h6>Master Document Upload</h6>
                    <p>Select from master database</p>
                  </div>
                </div>
                <div
                  className={`sd-upload-card${dragOver ? ' drag-over' : ''}`}
                  role='button'
                  onClick={onBrowse}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className='sd-upload-card-icon custom'>
                    <i className='ri-upload-cloud-2-line'></i>
                  </div>
                  <div className='sd-upload-card-text'>
                    <h6>Upload Custom File</h6>
                    <p>Browse from your device</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type='file'
                    hidden
                    accept='.pdf,.doc,.docx,.xls,.xlsx'
                    onChange={onFilePicked}
                  />
                </div>
              </Col>

              <Col sm={9}>
                <h6
                  className='text-uppercase fw-semibold mb-3 fs-12'
                  style={{ letterSpacing: '0.5px' }}
                >
                  List Data Document Requirement
                </h6>
                {reqDocs.length === 0 ? (
                  <div className='sd-empty'>
                    <i className='ri-file-text-line'></i>
                    <p>No documents uploaded yet</p>
                  </div>
                ) : (
                  <>
                    <div className='sd-doc-header'>
                      <span style={{ flex: '2 1 0' }}>Document Name</span>
                      <span style={{ flex: '1 1 0' }}>Source</span>
                      <span style={{ flex: '1 1 0' }}>Last Updated</span>
                      <span style={{ flex: '1 1 0', textAlign: 'center' }}>
                        Show to DMUT?
                      </span>
                      <span style={{ flex: '1.4 1 0', textAlign: 'right' }}>
                        Action
                      </span>
                    </div>
                    {reqDocs.map(d => (
                      <div key={d.id} className='sd-doc-row'>
                        <div style={{ flex: '2 1 0' }}>
                          <span className='fw-semibold fs-13'>{d.name}</span>
                        </div>
                        <div style={{ flex: '1 1 0' }}>
                          <span
                            className={`sd-badge ${
                              d.source === DOC_SOURCE.MASTER
                                ? 'master'
                                : 'custom'
                            }`}
                          >
                            {d.source === DOC_SOURCE.MASTER
                              ? 'Master Document'
                              : 'Upload Custom'}
                          </span>
                        </div>
                        <div style={{ flex: '1 1 0' }}>
                          <span className='text-muted fs-12'>
                            {formatDateTime(d.lastUpdated)}
                          </span>
                        </div>
                        <div
                          className='form-check form-switch form-switch-secondary d-flex justify-content-center mb-0'
                          style={{ flex: '1 1 0' }}
                        >
                          <Input
                            className='form-check-input'
                            type='checkbox'
                            role='switch'
                            checked={!!d.showToDmut}
                            onChange={() => toggleShowToDmut(d)}
                          />
                        </div>
                        <div style={{ flex: '1.4 1 0', textAlign: 'right' }}>
                          <div className='d-flex gap-1 justify-content-end'>
                            <button
                              className='sd-btn-ghost'
                              onClick={() => handleView(d)}
                            >
                              <i className='ri-eye-line'></i> View
                            </button>
                            <button
                              className='sd-btn-ghost danger'
                              disabled={busy}
                              onClick={() => setDeleteId(d.id)}
                            >
                              <i className='ri-delete-bin-6-line'></i> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>

      {/* Master document picker modal */}
      <Modal isOpen={masterModal} toggle={() => setMasterModal(false)} centered>
        <ModalHeader toggle={() => setMasterModal(false)}>
          Master Document Upload
        </ModalHeader>
        <ModalBody>
          <Input
            type='text'
            className='mb-3'
            placeholder='Search by document / file name...'
            value={masterSearch}
            onChange={e => setMasterSearch(e.target.value)}
          />
          <Table className='table-sm align-middle mb-0'>
            <thead className='table-light'>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Document Name</th>
              </tr>
            </thead>
            <tbody>
              {masterLoading ? (
                <tr>
                  <td colSpan={2} className='text-center text-muted py-3'>
                    <Spinner size='sm' className='me-2' />
                    Loading…
                  </td>
                </tr>
              ) : masterRows.length === 0 ? (
                <tr>
                  <td colSpan={2} className='text-center text-muted py-3'>
                    No documents found
                  </td>
                </tr>
              ) : (
                masterRows.map(m => (
                  <tr key={m.id}>
                    <td>
                      <Input
                        type='checkbox'
                        className='form-check-input'
                        checked={!!checkedMaster[m.id]}
                        onChange={() => toggleMaster(m.id)}
                      />
                    </td>
                    <td>
                      {/* Baris kaya mengikuti popup Master Document Planning:
                          nama bold + badge tipe + deskripsi + info file. */}
                      <span className='fw-semibold d-block'>
                        {m.documentName}
                      </span>
                      <div className='d-flex flex-wrap align-items-center gap-2 mt-1'>
                        {m.documentType && (
                          <span className='badge bg-secondary-subtle text-secondary'>
                            {m.documentType}
                          </span>
                        )}
                        {m.description && (
                          <small className='text-muted'>{m.description}</small>
                        )}
                      </div>
                      {m.fileName && (
                        <small className='text-primary d-block mt-1'>
                          <i className='ri-file-text-line me-1'></i>
                          {m.fileName}
                          {m.fileSize ? ` (${formatFileSize(m.fileSize)})` : ''}
                        </small>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button
            color='light'
            className='btn-sm'
            onClick={() => setMasterModal(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            className='btn-sm'
            onClick={addFromMaster}
            disabled={busy}
          >
            Add Document
          </Button>
        </ModalFooter>
      </Modal>

      {/* Custom file details modal */}
      <Modal isOpen={customModal} toggle={() => setCustomModal(false)} centered>
        <ModalHeader toggle={() => setCustomModal(false)}>
          Upload Custom File
        </ModalHeader>
        <ModalBody>
          <div className='mb-3'>
            <Label className='form-label'>Selected File</Label>
            <Input
              type='text'
              value={customFile?.name || ''}
              readOnly
              disabled
            />
          </div>
          <div className='mb-3'>
            <Label className='form-label'>
              Document Name <span className='text-danger'>*</span>
            </Label>
            <Input
              type='text'
              maxLength={250}
              value={customName}
              onChange={e => setCustomName(e.target.value)}
            />
          </div>
          <div className='form-check form-switch mb-1'>
            <Input
              type='checkbox'
              role='switch'
              className='form-check-input'
              id='proc-custom-show-dmut'
              checked={customShowToDmut}
              onChange={e => setCustomShowToDmut(e.target.checked)}
            />
            <Label for='proc-custom-show-dmut' className='form-check-label'>
              Show to DMUT
            </Label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color='light'
            className='btn-sm'
            onClick={() => setCustomModal(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            className='btn-sm'
            onClick={addCustom}
            disabled={busy}
          >
            Add Document
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        show={deleteId !== null}
        title='Delete Document'
        message='Are you sure you want to remove this document from the list?'
        confirmLabel='Delete'
        confirmColor='danger'
        onConfirm={confirmDelete}
        onClose={() => setDeleteId(null)}
      />
    </React.Fragment>
  );
};

export default ProcurementSupportingDocumentTab;
